<template>
  <div class="plan-step-page">
    <div class="page-shell">
      <section class="composer-panel" v-loading="submitting">
        <div class="panel-heading">
          <div>
            <p class="eyebrow">PLAN STEP</p>
            <h2>{{ editingId ? '编辑开发计划' : '录入开发计划' }}</h2>
          </div>
          <span class="soft-badge">{{ editingId ? '编辑中' : '初版' }}</span>
        </div>

        <div class="form-scroll">
          <el-form
            ref="planForm"
            :model="form"
            :rules="rules"
            label-position="top"
            class="plan-form"
            @submit.native.prevent
          >
            <el-form-item label="功能名称" prop="name">
              <el-input
                v-model.trim="form.name"
                placeholder="例如：驿站分组、饼图统计、菜单管理"
                maxlength="40"
                show-word-limit
              />
            </el-form-item>

            <el-form-item label="完成周期" prop="cycle">
              <div class="field-inline">
                <el-input-number
                  v-model="form.cycle"
                  :min="1"
                  :max="365"
                  controls-position="right"
                  class="cycle-input"
                />
                <span class="field-unit">天</span>
              </div>
            </el-form-item>

            <el-form-item label="完成时间" prop="deadline">
              <el-date-picker
                v-model="form.deadline"
                type="date"
                value-format="yyyy-MM-dd"
                placeholder="请选择计划完成时间"
                style="width: 100%"
              />
            </el-form-item>

            <el-form-item label="当前状态" prop="status">
              <el-select v-model="form.status" style="width: 100%">
                <el-option
                  v-for="item in currentFormStatusOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                />
              </el-select>
            </el-form-item>

            <el-form-item v-if="editingId" label="完成度">
              <div class="slider-box">
                <el-slider v-model="form.progress" :step="5" show-input />
              </div>
            </el-form-item>

            <el-form-item v-if="editingId" label="阶段说明">
              <el-input
                v-model.trim="form.summary"
                type="textarea"
                :rows="4"
                maxlength="200"
                show-word-limit
                placeholder="补充当前阶段产出、风险或下一步动作"
              />
            </el-form-item>

            <div v-if="!editingId" class="form-tip">
              <p>新建计划时不需要手填完成度和阶段说明。</p>
              <p>完成度会根据状态自动生成：计划中 20%，开发中 60%。</p>
            </div>

            <div class="form-actions">
              <el-button v-if="editingId" @click="cancelEdit">取消编辑</el-button>
              <el-button @click="resetForm">重置</el-button>
              <el-button type="primary" @click="submitPlan">
                {{ editingId ? '保存修改' : '加入计划' }}
              </el-button>
            </div>
          </el-form>
        </div>
      </section>

      <section class="board-panel">
        <div class="overview-card">
          <div class="overview-head">
            <div class="title-row">
              <!-- 4 字短标题：水墨打字不应用于长文本 -->
              <h1><ink-text text="开发进度" :stagger="45" :duration="460" /></h1>
            </div>
            <p class="overview-desc">右侧集中展示计划项，可按名称、状态或说明搜索，也可以通过状态标签快速筛选。</p>
          </div>

          <div class="metrics-grid">
            <div class="metric-item">
              <span class="metric-label">已完成</span>
              <strong class="metric-value">{{ completedCount }}</strong>
            </div>
            <div class="metric-item">
              <span class="metric-label">开发中</span>
              <strong class="metric-value">{{ progressingCount }}</strong>
            </div>
            <div class="metric-item">
              <span class="metric-label">计划中</span>
              <strong class="metric-value">{{ plannedCount }}</strong>
            </div>
            <div class="metric-item">
              <span class="metric-label">总计</span>
              <strong class="metric-value">{{ plans.length }}</strong>
            </div>
          </div>

          <div class="toolbar-row">
            <div class="search-wrap">
              <el-input
                v-model.trim="keyword"
                prefix-icon="el-icon-search"
                clearable
                placeholder="搜索功能名称、状态或说明..."
              />
            </div>

            <div class="filter-group">
              <button
                v-for="item in filterOptions"
                :key="item.value"
                type="button"
                class="filter-chip"
                :class="{ 'filter-chip--active': activeStatus === item.value }"
                @click="activeStatus = item.value"
              >
                {{ item.label }}
              </button>
            </div>

            <div class="layout-switch">
              <button
                v-for="item in layoutOptions"
                :key="item.value"
                type="button"
                class="layout-chip"
                :class="{ 'layout-chip--active': gridMode === item.value }"
                @click="gridMode = item.value"
              >
                {{ item.label }}
              </button>
            </div>
          </div>
        </div>

        <div class="list-panel" v-loading="loading">
          <div v-if="filteredPlans.length" class="plan-list" :class="planListClass">
            <article
              v-for="item in filteredPlans"
              :key="item.id"
              class="plan-card"
            >
              <div class="plan-card__header">
                <div class="plan-card__main">
                  <h3>{{ item.name }}</h3>
                  <p>{{ item.summary || '当前暂无补充说明。' }}</p>
                </div>
                <span class="task-status" :class="`task-status--${item.status}`">
                  {{ getStatusLabel(item.status) }}
                </span>
              </div>

              <div class="meta-row">
                <div class="meta-chip">
                  <span class="meta-chip__label">完成周期</span>
                  <strong>{{ item.cycle }} 天</strong>
                </div>
                <div class="meta-chip">
                  <span class="meta-chip__label">完成时间</span>
                  <strong>{{ formatDate(item.deadline) }}</strong>
                </div>
              </div>

              <div class="progress-wrap">
                <div class="progress-head">
                  <span>完成度</span>
                  <strong>{{ item.progress }}%</strong>
                </div>
                <el-progress
                  :percentage="item.progress"
                  :stroke-width="8"
                  :show-text="false"
                  :color="getProgressColor(item.status)"
                />
              </div>

              <div class="plan-card__actions">
                <el-button
                  type="text"
                  :disabled="item.status === 'done'"
                  @click="startEdit(item)"
                >
                  {{ item.status === 'done' ? '已完成不可编辑' : '编辑' }}
                </el-button>
                <el-button type="text" class="danger-text" @click="removePlan(item)">删除</el-button>
              </div>
            </article>
          </div>

          <el-empty
            v-else-if="!loading"
            description="没有匹配的计划项，换个关键词或筛选条件试试"
          />
        </div>
      </section>
    </div>
  </div>
</template>

<script>
// 水墨打字组件：仅用于 ≤20 字的短标题（本页「开发进度」4 字）
import InkText from '@/components/InkText.vue'

const STATUS_OPTIONS = [
  { label: '计划中', value: 'planned' },
  { label: '开发中', value: 'progress' },
  { label: '已完成', value: 'done' }
]

const FORM_STATUS_OPTIONS = [
  { label: '计划中', value: 'planned' },
  { label: '开发中', value: 'progress' }
]

const FILTER_OPTIONS = [
  { label: '全部', value: 'all' },
  { label: '计划中', value: 'planned' },
  { label: '开发中', value: 'progress' },
  { label: '已完成', value: 'done' }
]

const LAYOUT_OPTIONS = [
  { label: '1 列', value: 'single' },
  { label: '2 列', value: 'double' },
  { label: '4 列', value: 'quad' }
]

function createDefaultForm() {
  return {
    name: '',
    cycle: 3,
    deadline: '',
    status: 'planned',
    progress: 20,
    summary: ''
  }
}

export default {
  name: 'PlanStepIndex',
  components: {
    InkText
  },
  data() {
    return {
      loading: false,
      submitting: false,
      editingId: null,
      keyword: '',
      activeStatus: 'all',
      gridMode: 'single',
      statusOptions: STATUS_OPTIONS,
      formStatusOptions: FORM_STATUS_OPTIONS,
      filterOptions: FILTER_OPTIONS,
      layoutOptions: LAYOUT_OPTIONS,
      form: createDefaultForm(),
      plans: [],
      rules: {
        name: [
          { required: true, message: '请输入功能名称', trigger: 'blur' }
        ],
        cycle: [
          { required: true, message: '请输入完成周期', trigger: 'change' }
        ],
        deadline: [
          { required: true, message: '请选择完成时间', trigger: 'change' }
        ],
        status: [
          { required: true, message: '请选择当前状态', trigger: 'change' }
        ]
      }
    }
  },
  computed: {
    filteredPlans() {
      const keyword = this.keyword.toLowerCase()
      return this.plans.filter(item => {
        const matchesStatus = this.activeStatus === 'all' || item.status === this.activeStatus
        if (!matchesStatus) return false
        if (!keyword) return true
        const text = [
          item.name,
          item.summary,
          this.getStatusLabel(item.status),
          `${item.cycle}`,
          item.deadline
        ].join(' ').toLowerCase()
        return text.includes(keyword)
      })
    },
    completedCount() {
      return this.plans.filter(item => item.status === 'done').length
    },
    progressingCount() {
      return this.plans.filter(item => item.status === 'progress').length
    },
    plannedCount() {
      return this.plans.filter(item => item.status === 'planned').length
    },
    currentFormStatusOptions() {
      return this.editingId ? this.statusOptions : this.formStatusOptions
    },
    planListClass() {
      return `plan-list--${this.gridMode}`
    }
  },
  created() {
    this.fetchPlans()
  },
  methods: {
    async fetchPlans() {
      this.loading = true
      try {
        const response = await this.$axios.get('/api/plan-step/list')
        this.plans = Array.isArray(response.data && response.data.data) ? response.data.data : []
      } catch (error) {
        console.error('Failed to fetch plan list:', error)
        this.$toast.error('获取计划列表失败')
      } finally {
        this.loading = false
      }
    },
    resetForm() {
      this.editingId = null
      this.form = createDefaultForm()
      this.$nextTick(() => {
        if (this.$refs.planForm) this.$refs.planForm.clearValidate()
      })
    },
    cancelEdit() {
      this.resetForm()
    },
    getProgressByStatus(status) {
      return status === 'progress' ? 60 : 20
    },
    submitPlan() {
      this.$refs.planForm.validate(async valid => {
        if (!valid) return

        this.submitting = true
        try {
          const payload = {
            name: this.form.name,
            cycle: this.form.cycle,
            deadline: this.form.deadline,
            status: this.form.status,
            progress: this.editingId ? this.form.progress : this.getProgressByStatus(this.form.status),
            summary: this.form.summary
          }

          if (this.editingId) {
            const response = await this.$axios.put(`/api/plan-step/${this.editingId}`, payload)
            this.$toast.success((response.data && response.data.msg) || '计划项已更新')
          } else {
            const response = await this.$axios.post('/api/plan-step/create', payload)
            this.$toast.success((response.data && response.data.msg) || '计划项已加入列表')
          }

          await this.fetchPlans()
          this.resetForm()
        } catch (error) {
          const message = error.response && error.response.data && error.response.data.msg
          this.$toast.error(message || '提交失败，请稍后重试')
        } finally {
          this.submitting = false
        }
      })
    },
    startEdit(item) {
      if (item.status === 'done') {
        this.$toast.warning('已完成状态的计划项不能编辑')
        return
      }

      this.editingId = item.id
      this.form = {
        name: item.name,
        cycle: item.cycle,
        deadline: item.deadline,
        status: item.status,
        progress: Number(item.progress || 0),
        summary: item.summary || ''
      }

      this.$nextTick(() => {
        if (this.$refs.planForm) this.$refs.planForm.clearValidate()
      })
    },
    removePlan(item) {
      this.$confirm(`确定删除“${item.name}”吗？`, '提示', {
        type: 'warning'
      }).then(async () => {
        try {
          const response = await this.$axios.delete(`/api/plan-step/${item.id}`)
          if (this.editingId === item.id) this.resetForm()
          this.$toast.success((response.data && response.data.msg) || '计划项已删除')
          await this.fetchPlans()
        } catch (error) {
          const message = error.response && error.response.data && error.response.data.msg
          this.$toast.error(message || '删除失败，请稍后重试')
        }
      }).catch(() => {})
    },
    getStatusLabel(status) {
      const matched = this.statusOptions.find(item => item.value === status)
      return matched ? matched.label : '未定义'
    },
    getProgressColor(status) {
      if (status === 'done') return '#2f7cf6'
      if (status === 'progress') return '#55c271'
      return '#f0b24d'
    },
    formatDate(value) {
      return value || '--'
    }
  }
}
</script>

<style scoped>
.plan-step-page {
  /* 高度由内容区（.content-wrapper）统一提供：撑满容器即可，不再手算 calc(100vh - Npx) */
  height: 100%;
  padding: 16px;
  box-sizing: border-box;
  overflow: hidden;
  background:
    radial-gradient(circle at top left, rgba(123, 164, 255, 0.16), transparent 26%),
    linear-gradient(180deg, #f7f8ff 0%, #eff3fb 100%);
  border-radius: 8px;
}

.page-shell {
  display: grid;
  grid-template-columns: 330px minmax(0, 1fr);
  gap: 16px;
  height: 100%;
  overflow: hidden;
}

.composer-panel,
.board-panel {
  height: 100%;
  min-height: 0;
}

.composer-panel {
  display: flex;
  flex-direction: column;
  padding: 20px;
  border-radius: 24px;
  border: 1px solid rgba(31, 45, 61, 0.06);
  background: var(--surface-1);
  box-shadow: var(--shadow-surface-2);
  overflow: hidden;
}

.panel-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 14px;
}

.panel-heading h2 {
  margin: 4px 0 0;
  font-size: 24px;
  color: var(--color-text-primary);
}

.eyebrow {
  margin: 0;
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--color-text-secondary);
}

.soft-badge {
  padding: 7px 12px;
  border-radius: 999px;
  background: #edf4ff;
  color: #3164d6;
  font-size: 12px;
  font-weight: 600;
}

.form-scroll {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding-right: 6px;
}

.plan-form {
  display: flex;
  flex-direction: column;
}

.plan-form ::v-deep .el-form-item {
  margin-bottom: 18px;
}

.plan-form ::v-deep .el-form-item__label {
  padding-bottom: 6px;
  font-size: 14px;
}

.plan-form ::v-deep .el-input__inner {
  font-size: 14px;
}

.field-inline {
  display: flex;
  align-items: center;
  gap: 10px;
}

.cycle-input {
  width: 170px;
}

.field-unit {
  color: var(--color-text-regular);
  font-size: 13px;
}

.form-tip {
  margin-bottom: 18px;
  padding: 12px 14px;
  border-radius: 16px;
  background: linear-gradient(180deg, #f7fbff 0%, #eef4fb 100%);
  border: 1px solid #e2eaf6;
  color: #66758f;
  font-size: 12px;
  line-height: 1.7;
}

.form-tip p {
  margin: 0;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 2px;
}

.board-panel {
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 0;
  overflow: hidden;
}

.overview-card,
.list-panel {
  border-radius: 28px;
  border: 1px solid rgba(31, 45, 61, 0.06);
  background: var(--surface-1);
  box-shadow: var(--shadow-surface-2);
}

.overview-card {
  padding: 24px 28px 20px;
}

.overview-head {
  margin-bottom: 16px;
}

.title-row {
  margin-bottom: 8px;
}

.title-row h1 {
  margin: 0;
  font-size: 52px;
  line-height: 1;
  color: var(--color-text-primary);
  font-weight: 700;
}

.overview-desc {
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: var(--color-text-regular);
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 18px;
}

.metric-item {
  padding: 6px 0;
}

.metric-label {
  display: block;
  margin-bottom: 6px;
  color: var(--color-text-secondary);
  font-size: 13px;
}

.metric-value {
  color: var(--color-text-primary);
  font-size: 40px;
  line-height: 1;
  font-weight: 700;
  /* 等宽数字锁死：数值变化不引起布局抖动 */
  font-variant-numeric: tabular-nums;
  -webkit-font-variant-numeric: tabular-nums;
}

.toolbar-row {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}

.search-wrap {
  flex: 1 1 360px;
}

.search-wrap ::v-deep .el-input__inner {
  height: 58px;
  border-radius: 999px;
  border: 1px solid rgba(31, 45, 61, 0.1);
  padding-left: 50px;
  font-size: 16px;
  color: var(--color-text-primary);
  box-shadow: inset 0 1px 2px rgba(31, 45, 61, 0.04);
  transition: border-color var(--transition-duration-fast) var(--ease-out-soft),
    box-shadow var(--transition-duration-fast) var(--ease-out-soft);
}

.search-wrap ::v-deep .el-input__prefix {
  left: 16px;
  display: flex;
  align-items: center;
  font-size: 18px;
  color: var(--color-text-secondary);
}

.filter-group {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.layout-switch {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-chip {
  padding: 9px 16px;
  border: 1px solid rgba(31, 45, 61, 0.08);
  border-radius: 999px;
  background: #f8fbff;
  color: #66758f;
  font-size: 13px;
  cursor: pointer;
  transition: background-color var(--transition-duration-fast) var(--ease-out-soft),
    color var(--transition-duration-fast) var(--ease-out-soft),
    box-shadow var(--transition-duration-fast) var(--ease-out-soft);
}

.filter-chip--active {
  background: #eaf3ff;
  color: #3164d6;
  border-color: #bcd0f5;
  box-shadow: var(--shadow-surface-1);
}

.layout-chip {
  padding: 9px 14px;
  border: 1px solid rgba(31, 45, 61, 0.08);
  border-radius: 999px;
  background: var(--surface-1);
  color: #66758f;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.layout-chip--active {
  background: var(--color-primary);
  color: #fff;
  border-color: var(--color-primary);
}

.list-panel {
  flex: 1;
  min-height: 0;
  padding: 16px 18px;
  overflow: hidden;
}

.plan-list {
  display: grid;
  gap: 12px;
  max-height: 100%;
  overflow: auto;
  padding-right: 4px;
  align-content: start;
}

.plan-list--single {
  grid-template-columns: minmax(0, 1fr);
}

.plan-list--double {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.plan-list--quad {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.plan-card {
  padding: 18px 20px;
  border-radius: 22px;
  border: 1px solid rgba(31, 45, 61, 0.06);
  background: var(--surface-1);
  box-shadow: var(--shadow-surface-1);
}

.plan-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 14px;
}

.plan-card__main {
  min-width: 0;
}

.plan-card__header h3 {
  margin: 0 0 8px;
  font-size: 22px;
  color: var(--color-text-primary);
}

.plan-card__header p {
  margin: 0;
  font-size: 13px;
  line-height: 1.65;
  color: var(--color-text-regular);
}

.task-status {
  flex-shrink: 0;
  padding: 8px 16px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
}

.task-status--done {
  color: #5d97f5;
  background: #eaf3ff;
}

.task-status--progress {
  color: #46a76d;
  background: #e7faee;
}

.task-status--planned {
  color: #d18a2e;
  background: #fff5df;
}

.meta-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 14px;
}

.meta-chip {
  min-width: 140px;
  padding: 12px 14px;
  border-radius: 16px;
  background: #f4f7fc;
  border: 1px solid #e4ebf7;
}

.meta-chip__label {
  display: block;
  margin-bottom: 5px;
  color: var(--color-text-secondary);
  font-size: 11px;
}

.meta-chip strong {
  color: var(--color-text-primary);
  font-size: 15px;
}

.progress-wrap {
  padding-top: 2px;
}

.progress-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  color: var(--color-text-regular);
  font-size: 13px;
}

/* 进度百分比（{{ item.progress }}%）：等宽数字锁死，数值变化不抖动 */
.progress-head strong {
  font-variant-numeric: tabular-nums;
  -webkit-font-variant-numeric: tabular-nums;
}

.plan-card__actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 8px;
}

.danger-text {
  color: var(--color-danger);
}

@media (max-width: 1280px) {
  .page-shell {
    grid-template-columns: 300px minmax(0, 1fr);
  }

  .title-row h1 {
    font-size: 42px;
  }

  .metric-value {
    font-size: 34px;
  }

  .plan-list--quad {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 960px) {
  .page-shell {
    grid-template-columns: 1fr;
  }

  .plan-step-page {
    height: auto;
    min-height: calc(100vh - 120px);
    overflow: auto;
  }

  .composer-panel,
  .overview-card,
  .list-panel {
    border-radius: 22px;
  }

  .metrics-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .toolbar-row {
    align-items: stretch;
  }

  .layout-switch {
    width: 100%;
    justify-content: flex-start;
  }

  .plan-list--double,
  .plan-list--quad {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
